import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, Download } from "lucide-react";
import { Pagination } from "antd";
import { Button } from "../../components/ui/button";
import { downloadCSV } from "@/lib/downloadCSV";
import { notify } from "@/components/common/Notification";
import { PtsChangeRequestService } from "@/services/APIs";
import { Card, CardContent } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { ChangeRequestStatusBadge } from "./StatusBadge";
import ChangeRequestFilters from "./ChangeRequestFilters";
import {
  EMPTY_CHANGE_REQUEST_FILTERS,
  type ChangeRequestFilterValues,
} from "./ChangeRequestFilters.constants";
import {
  formatDateYmd,
  formatHours,
  formatInr,
  monthLabel,
  monthName,
  resolveName,
} from "./utils";

type AccountantTabProps = {
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
};

function isInvoiceRaisedYes(
  value?: "yes" | "no" | boolean | string | null
): boolean {
  if (value === true) {
    return true;
  }
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "yes" || normalized === "true" || normalized === "1";
}

const AccountantTab = ({
  projectNameById,
  employeeNameById,
}: AccountantTabProps) => {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [monthIndex, setMonthIndex] = useState(() => new Date().getMonth());
  const [filterValues, setFilterValues] = useState<ChangeRequestFilterValues>(
    EMPTY_CHANGE_REQUEST_FILTERS
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues, year, monthIndex]);

  const accountFilters = useMemo(
    () => ({
      month: monthName(monthIndex),
      year,
    }),
    [monthIndex, year]
  );

  const monthOptions = useMemo(() => {
    const options: {
      value: string;
      label: string;
      year: number;
      month: number;
    }[] = [];
    const base = new Date();
    for (let offset = 0; offset < 18; offset += 1) {
      const date = new Date(base.getFullYear(), base.getMonth() - offset, 1);
      options.push({
        value: `${date.getFullYear()}-${date.getMonth()}`,
        label: monthLabel(date.getFullYear(), date.getMonth()),
        year: date.getFullYear(),
        month: date.getMonth(),
      });
    }
    return options;
  }, []);

  const { data: accountData, isLoading: isAccountLoading } = useQuery({
    queryKey: ["change-requests", "account", accountFilters],
    queryFn: () =>
      PtsChangeRequestService.getAccountChangeRequests(accountFilters),
  });

  const { data: entriesData, isLoading: isEntriesLoading } = useQuery({
    queryKey: [
      "change-requests",
      "accountant-entries",
      currentPage,
      pageSize,
      year,
      monthIndex,
      filterValues.projectId,
      filterValues.status,
      filterValues.raisedToClientStatus,
    ],
    queryFn: () =>
      PtsChangeRequestService.getChangeRequests({
        page: currentPage,
        page_size: pageSize,
        month: monthName(monthIndex),
        year: year,
        status: filterValues.status ?? "Approved",
        project: filterValues.projectId,
        raised_to_client_status: filterValues.raisedToClientStatus,
      }),
  });

  const monthEntries = entriesData?.results ?? [];
  const totalCount = entriesData?.count ?? 0;

  const cards = accountData?.cards ?? {};
  const cumulativeRows = accountData?.results ?? [];

  const invoiceMutation = useMutation({
    mutationFn: ({
      project,
      invoice_raised_status,
    }: {
      project: string;
      invoice_raised_status: boolean;
    }) =>
      PtsChangeRequestService.updateAccountInvoiceRaised({
        project,
        filters: accountFilters,
        invoice_raised_status,
      }),
    onSuccess: result => {
      if (!result) {
        return;
      }
      notify.success("Invoice raised status updated");
      queryClient.invalidateQueries({
        queryKey: ["change-requests", "account"],
      });
    },
  });

  const handleExportCumulativeCSV = () => {
    const escapeCsv = (val: any) => {
      const str = val === null || val === undefined ? "" : String(val);
      if (
        str.includes(",") ||
        str.includes('"') ||
        str.includes("\n") ||
        str.includes("\r")
      ) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = [
      "Project",
      "Change Requests",
      "Total Hours",
      "Total Cost",
      "Invoice Raised",
    ];

    const rows = cumulativeRows.map(row => {
      const projName =
        row.project_name ?? resolveName(row.project, projectNameById);
      const invoiceYes = isInvoiceRaisedYes(row.invoice_raised_status)
        ? "Yes"
        : "No";
      return [
        escapeCsv(projName),
        escapeCsv(row.change_request),
        escapeCsv(row.total_hours),
        escapeCsv(row.total_cost),
        escapeCsv(invoiceYes),
      ];
    });

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join(
      "\n"
    );

    downloadCSV(
      csvContent,
      `change-requests-cumulative-${monthName(monthIndex)}-${year}`
    );
  };

  const handleExportEntriesCSV = async () => {
    try {
      const res = await PtsChangeRequestService.getChangeRequests({
        page_size: 100000,
        month: monthName(monthIndex),
        year: year,
        status: filterValues.status ?? "Approved",
        project: filterValues.projectId,
        raised_to_client_status: filterValues.raisedToClientStatus,
      });

      const items = res.results;

      const escapeCsv = (val: any) => {
        const str = val === null || val === undefined ? "" : String(val);
        if (
          str.includes(",") ||
          str.includes('"') ||
          str.includes("\n") ||
          str.includes("\r")
        ) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const headers = [
        "CR Number",
        "Project Name",
        "Title",
        "Priority",
        "Requested Date",
        "Expected Completion Date",
        "Assign Employees",
        "Estimated Hours",
        "Description",
        "Status",
        "BA Name",
        "Cost",
        "Billing Configured",
        "Invoice Raised Status",
        "Raised to Client Status",
        "Raised Date",
        "Created On",
        "Updated On",
      ];

      const rows = items.map(item => {
        const projName =
          item.project_name ?? resolveName(item.project, projectNameById);
        const baName = item.ba_name ?? resolveName(item.ba, employeeNameById);
        const invoiceYes = isInvoiceRaisedYes(item.invoice_raised_status)
          ? "Yes"
          : item.invoice_raised_status === false ||
              String(item.invoice_raised_status ?? "")
                .trim()
                .toLowerCase() === "no"
            ? "No"
            : "—";

        const rawEmployees = item.assign_employees;
        const employeesList = Array.isArray(rawEmployees)
          ? rawEmployees
              .map((id: string) => employeeNameById.get(id) ?? id)
              .join(", ")
          : typeof rawEmployees === "string"
            ? (rawEmployees as string)
                .split(",")
                .map(
                  (id: string) => employeeNameById.get(id.trim()) ?? id.trim()
                )
                .join(", ")
            : "";

        return [
          escapeCsv(item.cr_number),
          escapeCsv(projName),
          escapeCsv(item.title),
          escapeCsv(item.priority),
          escapeCsv(formatDateYmd(item.requested_date)),
          escapeCsv(formatDateYmd(item.expected_completion_date)),
          escapeCsv(employeesList),
          escapeCsv(item.estimated_hours),
          escapeCsv(item.description),
          escapeCsv(item.status),
          escapeCsv(baName),
          escapeCsv(item.cost),
          escapeCsv(item.billing_configured),
          escapeCsv(invoiceYes),
          escapeCsv(item.raised_to_client_status),
          escapeCsv(formatDateYmd(item.raised_date)),
          escapeCsv(formatDateYmd(item.created_on)),
          escapeCsv(formatDateYmd(item.updated_on)),
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map(r => r.join(",")),
      ].join("\n");

      downloadCSV(
        csvContent,
        `change-requests-entries-${monthName(monthIndex)}-${year}`
      );
    } catch {
      notify.error("Failed to export CSV");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Accountant dashboard
        </h1>
        <div className="w-full sm:w-56">
          <Select
            value={`${year}-${monthIndex}`}
            onValueChange={value => {
              const [y, m] = value.split("-").map(Number);
              setYear(y);
              setMonthIndex(m);
            }}
          >
            <SelectTrigger className="h-10 bg-white">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <SelectValue placeholder="Month" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-white">
              {monthOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Change requests this month</p>
            <p className="mt-2 text-3xl font-bold text-[#111827]">
              {cards.change_requests_this_month ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-gray-500">Total hours this month</p>
            <p className="mt-2 text-3xl font-bold text-[#111827]">
              {cards.total_hours_this_month ?? 0} hrs
            </p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-5">
            <p className="text-sm text-sky-700/80">Total cost this month</p>
            <p className="mt-2 text-3xl font-bold text-[#111827]">
              {formatInr(cards.total_cost_this_month ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Cumulative by project
          </h2>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleExportCumulativeCSV}
            className="h-8 px-3 text-xs"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export Cumulative
          </Button>
        </div>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3 font-medium">Project</th>
                    <th className="px-5 py-3 font-medium">Change Requests</th>
                    <th className="px-5 py-3 font-medium">Total Hours</th>
                    <th className="px-5 py-3 font-medium">Total Cost</th>
                    <th className="px-5 py-3 font-medium">Invoice Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {isAccountLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : cumulativeRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        Nothing forwarded for this month.
                      </td>
                    </tr>
                  ) : (
                    cumulativeRows.map(row => {
                      const invoiceYes = isInvoiceRaisedYes(
                        row.invoice_raised_status
                      );
                      const isUpdating =
                        invoiceMutation.isPending &&
                        invoiceMutation.variables?.project === row.project;

                      return (
                        <tr
                          key={row.project}
                          className="border-b border-gray-50 last:border-0"
                        >
                          <td className="px-5 py-3.5 font-medium text-[#111827]">
                            {row.project_name ??
                              resolveName(row.project, projectNameById)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {row.change_request ?? 0}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {formatHours(row.total_hours)}
                          </td>
                          <td className="px-5 py-3.5 text-[#374151]">
                            {formatInr(row.total_cost)}
                          </td>
                          <td className="px-5 py-3.5">
                            <Select
                              value={invoiceYes ? "yes" : "no"}
                              disabled={isUpdating}
                              onValueChange={value =>
                                invoiceMutation.mutate({
                                  project: row.project,
                                  invoice_raised_status: value === "yes",
                                })
                              }
                            >
                              <SelectTrigger
                                className={`h-8 w-[80px] rounded-2xl border-0 text-xs font-semibold ${
                                  invoiceYes
                                    ? "border border-green-300 bg-green-100 text-green-700"
                                    : "border border-red-300 bg-red-100 text-red-700"
                                }`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Change request entries
          </h2>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleExportEntriesCSV}
            className="h-8 px-3 text-xs"
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export Entries
          </Button>
        </div>
        <ChangeRequestFilters
          values={filterValues}
          onChange={setFilterValues}
          idPrefix="accountant-change-request"
        />
        <Card className="border-gray-200 bg-white shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-5 py-3 font-medium">CR Number</th>
                    <th className="px-5 py-3 font-medium">Project</th>
                    <th className="px-5 py-3 font-medium">Title</th>
                    <th className="px-5 py-3 font-medium">Estimated Hours</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Requested Date</th>
                    <th className="px-5 py-3 font-medium">
                      Raised to Client Status
                    </th>
                    <th className="px-5 py-3 font-medium">Raised Date</th>
                    <th className="px-5 py-3 font-medium">BA</th>
                    <th className="px-5 py-3 font-medium">Cost</th>
                    <th className="px-5 py-3 font-medium">Invoice Raised</th>
                  </tr>
                </thead>
                <tbody>
                  {isEntriesLoading ? (
                    <tr>
                      <td
                        colSpan={11}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : monthEntries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={11}
                        className="px-5 py-10 text-center text-gray-500"
                      >
                        No approved change requests this month.
                      </td>
                    </tr>
                  ) : (
                    monthEntries.map(item => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="px-5 py-3.5 font-medium text-[#111827]">
                          {item.cr_number || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.project_name ??
                            resolveName(item.project, projectNameById)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.title || "—"}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatHours(item.estimated_hours)}
                        </td>
                        <td className="px-5 py-3.5">
                          <ChangeRequestStatusBadge status={item.status} />
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatDateYmd(item.requested_date)}
                        </td>
                        <td className="px-5 py-3.5">
                          {item.raised_to_client_status ? (
                            <ChangeRequestStatusBadge
                              status={item.raised_to_client_status}
                            />
                          ) : (
                            <span className="text-[#374151]">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatDateYmd(item.raised_date)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {item.ba_name ??
                            resolveName(item.ba, employeeNameById)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          {formatInr(item.cost)}
                        </td>
                        <td className="px-5 py-3.5 text-[#374151]">
                          <span
                            className={`inline-flex items-center text-sm font-medium ${
                              item.invoice_raised_status === "yes"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {isInvoiceRaisedYes(item.invoice_raised_status)
                              ? "Yes"
                              : item.invoice_raised_status === false ||
                                  String(item.invoice_raised_status ?? "")
                                    .trim()
                                    .toLowerCase() === "no"
                                ? "No"
                                : "—"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalCount > 0 && (
              <div className="flex items-center justify-end border-t p-4">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={totalCount}
                  onChange={(page, size) => {
                    if (size && size !== pageSize) {
                      setPageSize(size);
                      setCurrentPage(1);
                      return;
                    }
                    setCurrentPage(page);
                  }}
                  onShowSizeChange={(_current, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                  pageSizeOptions={["50", "100", "200", "500"]}
                  defaultPageSize={100}
                  disabled={isEntriesLoading}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccountantTab;
